import { ref } from 'vue'
import { defineStore } from 'pinia'
import UserActionsService from '../services/userActionsService'
import { CustomerPermissionsStore } from '@/stores/customerPermissionsStore'
import { isUserActionsResponse, UserActionsResponse } from '@/types/userActions/userActionsResponse'
import { USER_ACTIONS_CATALOG, type UserActionKey } from '@/types/userActions/userActionCatalog'

function normalizeActionName(name: string): string {
  return name.replace(/\s+/g, '').toLocaleLowerCase()
}

export const UserActionsStore = defineStore(
  'userActions',
  () => {
    const userActions = ref<Record<string, UserActionsResponse[]>>({})
    const isError = ref<boolean>(false)

    async function fetchUserActions(relationContactId: number): Promise<boolean> {
      try {
        const actionsResponse = await UserActionsService.getUserActions(relationContactId)
        if (!isUserActionsResponse(actionsResponse)) {
          isError.value = true
          return false
        }
        userActions.value[relationContactId] = actionsResponse
        isError.value = false
        return true
      } catch (error) {
        console.error('Failed to refresh user actions:', error)
        isError.value = true
        return false
      }
    }

    function actionsForSelectedCompany(): UserActionsResponse[] {
      const relationContactId = CustomerPermissionsStore().selectedRelationContactId
      if (!relationContactId) {
        return []
      }
      return userActions.value[relationContactId] || []
    }

    function can(key: UserActionKey): boolean {
      const catalogItem = USER_ACTIONS_CATALOG[key]
      const needle = normalizeActionName(catalogItem.name)
      return actionsForSelectedCompany().some(
        (action) => action.id === catalogItem.id || normalizeActionName(action.name) === needle,
      )
    }

    function clearUserActions() {
      userActions.value = {}
    }

    return {
      userActions,
      isError,
      can,
      fetchUserActions,
      clearUserActions,
    }
  },
  {
    persist: {
      storage: sessionStorage,
      pick: ['userActions', 'isError'],
    },
  },
)
